import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../axiosInstance/axiosInstance";

import axios, { AxiosError, HttpStatusCode } from "axios";
import { UserLoginRequest } from "./UserLoginRequest";
import { UserLoginResponse } from "./UserLoginResponse";
import { UserRegisterRequest } from "./UserRegisterRequest";
import { RegisterErrorResponse } from "./RegisterErrorResponse";
import { clearAllCookies } from "../extensions/clearAllCookies";
import { ForgotPasswordRequest } from "./ForgotPasswordRequest";
import { ResetPasswordRequest } from "./ResetPasswordRequest";
import { ProfileNameResponse } from "./ProfileNameResponse";
import { UpdateProfileNameRequest } from "./UpdateProfileNameRequest";
import { PasswordlessSignInRequest } from "./PasswordlessSignInRequest";
import { RedeemSignInTokenRequest } from "./RedeemSignInTokenRequest";
import { VerifySignInCodeRequest } from "./VerifySignInCodeRequest";
import { StartTrialRequest } from "./StartTrialRequest";

export const registerUserAsync = createAsyncThunk<
  { registered: boolean },
  UserRegisterRequest,
  { rejectValue: RegisterErrorResponse }
>(
  "authentication/registerUser",
  async (userData: UserRegisterRequest, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post<{
        success: boolean;
        message?: string;
      }>("/account/register", userData);
      if (response.status !== HttpStatusCode.Ok) {
        return rejectWithValue({
          status: response.status,
          message: response.data.message || "Registration failed",
        });
      }
      return { registered: true };
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response) {
          return rejectWithValue({
            status: error.response.status as HttpStatusCode,
            message: "An unknown error occurred during registration.",
          });
        }
        return rejectWithValue({
          status: HttpStatusCode.InternalServerError,
          message: error.message,
        });
      }
      return rejectWithValue({
        status: HttpStatusCode.InternalServerError,
        message: "An unknown error occurred during registration",
      });
    }
  }
);

export const loginUserAsync = createAsyncThunk<
  UserLoginResponse,
  UserLoginRequest,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/loginUser", async (userLoginRequest, { rejectWithValue }) => {
  try {
    clearAllCookies();
    const response = await axiosInstance.post<UserLoginResponse>(
      "/account/login",
      userLoginRequest
    );

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue({
        status: response.status,
        message: "Login failed",
      });
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Login error",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unknown error occurred during login",
    });
  }
});

/**
 * Requests a passwordless sign-in email (magic link + code). Always resolves —
 * the API never reveals whether the email is known.
 */
export const requestSignInEmailAsync = createAsyncThunk<
  void,
  string,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/requestSignInEmail", async (email, { rejectWithValue }) => {
  try {
    const request: PasswordlessSignInRequest = { email };
    await axiosInstance.post("/account/passwordless/request", request);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Sign-in email request failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unknown error occurred while requesting the sign-in email",
    });
  }
});

/**
 * Starts a trial from an email alone: the API creates the account when the address
 * is new and mails a single-use sign-in link + code (or the ordinary sign-in email
 * for a known address — the response never tells the two apart). Rejects with the
 * status so the form can explain a reCAPTCHA (403) or rate-limit (429) refusal.
 */
export const startTrialAsync = createAsyncThunk<
  void,
  StartTrialRequest,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/startTrial", async (request, { rejectWithValue }) => {
  try {
    await axiosInstance.post("/account/passwordless/start-trial", request);
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Trial start failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unknown error occurred while starting the trial",
    });
  }
});

/** Redeems a passwordless magic-link token; resolves to the same login payload as password login. */
export const redeemSignInTokenAsync = createAsyncThunk<
  UserLoginResponse,
  string,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/redeemSignInToken", async (token, { rejectWithValue }) => {
  try {
    clearAllCookies();
    const request: RedeemSignInTokenRequest = { token };
    const response = await axiosInstance.post<UserLoginResponse>(
      "/account/passwordless/redeem",
      request
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Sign-in link redeem failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unknown error occurred during sign-in",
    });
  }
});

/** Completes a passwordless login with the short code from the sign-in email. */
export const verifySignInCodeAsync = createAsyncThunk<
  UserLoginResponse,
  VerifySignInCodeRequest,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/verifySignInCode", async (request, { rejectWithValue }) => {
  try {
    clearAllCookies();
    const response = await axiosInstance.post<UserLoginResponse>(
      "/account/passwordless/verify-code",
      request
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Sign-in code verification failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unknown error occurred during sign-in",
    });
  }
});

export const resetPasswordAsync = createAsyncThunk<
  { success: boolean; message: string },
  string,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/resetPassword", async (email: string, { rejectWithValue }) => {
  try {
    const request: ForgotPasswordRequest = { email };
    const response = await axiosInstance.post<{ message: string }>(
      "/account/forgot-password",
      request
    );

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue({
        status: response.status,
        message: "Password reset request failed",
      });
    }

    return { success: true, message: response.data.message };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Password reset request failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unexpected error occurred during password reset request",
    });
  }
});

export const resetPasswordConfirmAsync = createAsyncThunk<
  { success: boolean; message: string },
  ResetPasswordRequest,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/resetPasswordConfirm", async (request: ResetPasswordRequest, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<{ message: string }>(
      "/account/reset-password",
      request
    );

    if (response.status !== HttpStatusCode.Ok) {
      return rejectWithValue({
        status: response.status,
        message: "Password reset failed",
      });
    }

    return { success: true, message: response.data.message };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Password reset failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unexpected error occurred during password reset",
    });
  }
});

export const updateProfileNameAsync = createAsyncThunk<
  ProfileNameResponse,
  UpdateProfileNameRequest,
  { rejectValue: { status: HttpStatusCode; message: string } }
>("authentication/updateProfileName", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<ProfileNameResponse>("/profile/name", request);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return rejectWithValue({
        status: error.response.status as HttpStatusCode,
        message: error.response.data?.message || "Profile name update failed",
      });
    }

    return rejectWithValue({
      status: HttpStatusCode.InternalServerError,
      message: "An unexpected error occurred while updating the profile name",
    });
  }
});
